import type { MaskitoPlugin, MaskitoElement } from "@maskito/core";
import { maskitoUpdateElement } from "@maskito/core";

import { YearlessDateMode } from "../../types";
import { getDaysInMonth } from "../utils";

type YearlessDateSegments = {
  month: number;
  day: number;
};

type SegmentRange = [number, number];

// Constants
const MIN_MONTH = 1;
const MAX_MONTH = 12;
const MIN_DAY = 1;
const MAX_DAY = 31;
const SEGMENT_LENGTH = 2;

/**
 * Creates a Maskito plugin for yearless date segment stepping
 * @param mode - The date format mode (mm/dd or dd/mm)
 * @returns MaskitoPlugin that handles arrow key navigation
 * @example
 * const plugin = createYearlessDateSegmentsSteppingPlugin({ mode: "mm/dd" });
 */
export function createYearlessDateSegmentsSteppingPlugin({
  mode,
}: {
  mode: YearlessDateMode;
}): MaskitoPlugin {
  const segmentsIndexes = createSegmentIndexes(mode);

  return (element) => {
    const listener = (event: KeyboardEvent): void => {
      if (event.key === "ArrowRight") {
        handleArrowRight(element, event);
        return;
      }

      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") {
        return;
      }

      event.preventDefault();

      const selectionStart = element.selectionStart ?? 0;
      const activeSegment = getActiveSegment({
        segmentsIndexes,
        selectionStart,
      });

      if (!activeSegment) {
        return;
      }

      const segmentSelection = segmentsIndexes.get(activeSegment);
      if (!segmentSelection) {
        return;
      }

      const maxValue = getMaxValueForSegment(
        activeSegment,
        element.value,
        segmentsIndexes,
      );

      const updatedValue = updateSegmentValue({
        selection: segmentSelection,
        value: element.value,
        increment: event.key === "ArrowUp" ? -1 : 1,
        min: MIN_DAY,
        max: maxValue,
      });

      // If we're updating the month, check if the day needs to be adjusted
      const finalValue =
        activeSegment === "month"
          ? adjustDayForNewMonth(updatedValue, segmentsIndexes)
          : updatedValue;

      maskitoUpdateElement(element, {
        value: finalValue,
        selection: [selectionStart, selectionStart],
      });
    };

    element.addEventListener("keydown", listener);
    return () => element.removeEventListener("keydown", listener);
  };
}

function createSegmentIndexes(
  mode: YearlessDateMode,
): Map<keyof YearlessDateSegments, SegmentRange> {
  const map = new Map<keyof YearlessDateSegments, SegmentRange>();

  if (mode === "mm/dd") {
    map.set("month", [0, SEGMENT_LENGTH]);
    map.set("day", [SEGMENT_LENGTH + 1, SEGMENT_LENGTH * 2 + 1]);
  } else if (mode === "dd/mm") {
    map.set("day", [0, SEGMENT_LENGTH]);
    map.set("month", [SEGMENT_LENGTH + 1, SEGMENT_LENGTH * 2 + 1]);
  }

  return map;
}

/**
 * Handle ArrowRight key to insert separator at the right position
 */
function handleArrowRight(element: MaskitoElement, event: KeyboardEvent): void {
  const selectionStart = element.selectionStart ?? 0;
  const value = element.value;

  // Position 3 is where the separator should be (after the first segment)
  if (selectionStart === SEGMENT_LENGTH && value.includes("__")) {
    // Insert the separator
    event.preventDefault();
    maskitoUpdateElement(element, {
      value: value + "/",
      selection: [selectionStart + 1, selectionStart + 1],
    });
  }
}

/**
 * Adjust the day value if it's out of bounds for the new month
 */
function adjustDayForNewMonth(
  value: string,
  segmentsIndexes: Map<keyof YearlessDateSegments, SegmentRange>,
): string {
  const monthSelection = segmentsIndexes.get("month");
  const daySelection = segmentsIndexes.get("day");

  if (!monthSelection || !daySelection) {
    return value;
  }

  const monthValue = parseSegmentValue(value, monthSelection);
  const dayValue = parseSegmentValue(value, daySelection);

  // If month or day is invalid, return original value
  if (monthValue < MIN_MONTH || monthValue > MAX_MONTH || dayValue < MIN_DAY) {
    return value;
  }

  const maxDaysInMonth = getDaysInMonth(monthValue);

  // If day is within bounds, no adjustment needed
  if (dayValue <= maxDaysInMonth) {
    return value;
  }

  // Adjust day to the last valid day of the month
  const [dayFrom, dayTo] = daySelection;
  const adjustedDay = String(maxDaysInMonth).padStart(dayTo - dayFrom, "0");

  return (
    value.slice(0, dayFrom) + adjustedDay + value.slice(dayTo, value.length)
  );
}

/**
 * Parse a segment value from the input string, treating empty segments as 0
 */
function parseSegmentValue(value: string, selection: SegmentRange): number {
  const [from, to] = selection;
  const segmentText = value.slice(from, to);
  if (segmentText.includes("_")) return 0;
  return Number(segmentText.padEnd(to - from, "0"));
}

function getMaxValueForSegment(
  segment: keyof YearlessDateSegments,
  value: string,
  segmentsIndexes: Map<keyof YearlessDateSegments, SegmentRange>,
): number {
  if (segment === "month") {
    return MAX_MONTH;
  }

  // For day segment, get the current month value to determine max days
  const monthSelection = segmentsIndexes.get("month");
  if (!monthSelection) {
    return MAX_DAY;
  }

  const monthValue = parseSegmentValue(value, monthSelection);

  // If month is not valid, use default max
  if (monthValue < MIN_MONTH || monthValue > MAX_MONTH) {
    return MAX_DAY;
  }

  return getDaysInMonth(monthValue);
}

function getActiveSegment({
  segmentsIndexes,
  selectionStart,
}: {
  segmentsIndexes: Map<keyof YearlessDateSegments, SegmentRange>;
  selectionStart: number;
}): keyof YearlessDateSegments | null {
  for (const [segmentName, segmentRange] of segmentsIndexes.entries()) {
    const [from, to] = segmentRange;
    if (from <= selectionStart && selectionStart <= to) {
      return segmentName;
    }
  }
  return null;
}

function updateSegmentValue({
  selection,
  value,
  increment,
  min,
  max,
}: {
  selection: SegmentRange;
  value: string;
  increment: number;
  min: number;
  max: number;
}): string {
  const [from, to] = selection;
  const segmentValue = parseSegmentValue(value, selection);
  // Prevent decrementing 0 losing negative and rolling up to 1.
  if (segmentValue === 0 && increment < 0) return value;
  const newSegmentValue = Math.max(
    min,
    Math.min(max, segmentValue + increment),
  );

  return (
    value.slice(0, from) +
    String(newSegmentValue).padStart(to - from, "0") +
    value.slice(to, value.length)
  );
}
